import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Tabs } from 'antd';
import OmniNotification from 'components/OmniNotification';
import { getAPISorter } from 'utils/tableUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import styles from './RedemptionCodesPage.module.less';
import api, {
	ExportType,
	FileResponse,
	MgCodeDto,
	MgCodeListSortField,
	ProductStatus,
	RedemptionCodeDto,
	RedemptionCodeListSortField,
} from 'api';
import { DeleteOutlined } from '@ant-design/icons';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import RedemptionCodesCreateModal from './RedemptionCodesCreateModal';
import RedemptionCodesTab from './RedemptionCodesTab';
import MinimumGuaranteedCodesTab from './MinimumGuaranteedCodesTab';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import { useParams } from 'react-router-dom';

type ParamsProps = {
	projectId: string;
};

const { TabPane } = Tabs;

enum RedemptionCodeTabs {
	RedemptionCode = 'RedemptionCode',
	MinimumGuaranteedCode = 'MinimumGuaranteedCode',
}

export default function RedemptionCodesPage() {
	const { t } = useTranslation();
	const projectId = parseInt(useParams<ParamsProps>().projectId);
	const projectInfo = useSelector(projectSelector);
	const [activeTab, setActiveTab] = useState(RedemptionCodeTabs.RedemptionCode);
	const [createModalVisible, setCreateModalVisible] = useState(false);
	const [ifRefetchDataOfMgCodes, setIfRefetchDataOfMgCodes] = useState(false);
	const [ifRefetchDataOfOmniOnline, setIfRefetchDataOfRedemptionCodes] =
		useState(false);
	const createModalOpen = useCallback(() => {
		setCreateModalVisible(true);
	}, [createModalVisible]);
	const [redemptionCodesSorterRef, setRedemptionCodeSorterRef] = useState<
		SorterResult<RedemptionCodeDto> | SorterResult<RedemptionCodeDto>[]
	>({
		order: 'descend' as SortOrder,
		column: {
			APISortField: RedemptionCodeListSortField.CreatedAt,
		} as any,
	});
	const [mgCodesSorterRef, setMgCodesSorterRef] = useState<
		SorterResult<MgCodeDto> | SorterResult<MgCodeDto>[]
	>({
		order: 'descend' as SortOrder,
		column: {
			APISortField: MgCodeListSortField.CreatedAt,
		} as any,
	});
	const [hasMgCodes, setHasMgCodes] = useState(false);
	const [hasNoData, setHasNoData] = useState(true);
	const [selectedRows, setSelectedRows] = useState<
		RedemptionCodeDto[] | MgCodeDto[]
	>([]);
	const [isCSVDownloading, setIsCSVDownloading] = useState(false);
	const [isBatchDeleteing, setIsBatchDeleteing] = useState(false);

	const handleBatchDelete = async () => {
		deleteModal(selectedRows);
	};

	const handleExportClick = async () => {
		setIsCSVDownloading(true);

		let exportPromise: Promise<FileResponse>;
		if (activeTab === RedemptionCodeTabs.RedemptionCode) {
			exportPromise = api.exportRedemptionCodes(
				projectId,
				getAPISorter(redemptionCodesSorterRef).sortField,
				getAPISorter(redemptionCodesSorterRef).sortOrder,
				ExportType.Csv,
				undefined,
			);
		} else {
			exportPromise = api.exportMgCodesByContentProvider(
				getAPISorter(mgCodesSorterRef).sortField,
				getAPISorter(mgCodesSorterRef).sortOrder,
				projectInfo?.productId!,
				undefined,
			);
		}

		exportPromise
			.then((res) => {
				downloadFileByObjectURL(URL.createObjectURL(res.data), res.fileName);
			})
			.catch(() => {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: t('api.defaultError'),
				});
			})
			.finally(() => {
				setIsCSVDownloading(false);
			});
	};

	const deleteModal = (redemptionCodes: RedemptionCodeDto[]) => {
		Modal.confirm({
			title: (
				<span className={styles.warningModalHeader}>
					{t('redemptionCodesPage.DeleteRedemptionCodeModal.Header')}
				</span>
			),
			okText: t('redemptionCodesPage.DeleteRedemptionCodeModal.OkButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleBatchDeleteRedemptionCode(redemptionCodes);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('redemptionCodesPage.DeleteRedemptionCodeModal.Body', {
						num: redemptionCodes.length,
					})}
				</span>
			),
		});
	};

	const handleBatchDeleteRedemptionCode = async (
		redemptionCodes: (RedemptionCodeDto | MgCodeDto)[],
	) => {
		if (
			!redemptionCodes ||
			redemptionCodes.length <= 0 ||
			activeTab !== RedemptionCodeTabs.RedemptionCode
		)
			return;
		const ids = redemptionCodes
			.filter((x) => !!x.id)
			.map((x) => x.id as number);

		setIsBatchDeleteing(true);
		api
			.deleteRedemptionCodes(projectId, ids)
			.then(() => {
				OmniNotification.success({
					message: t(
						'redemptionCodesPage.DeleteRedemptionCodeModal.Success.Header',
					),
					description: t(
						'redemptionCodesPage.DeleteRedemptionCodeModal.Success.Body',
						{ count: ids.length },
					),
				});
				setSelectedRows([]);
				if (activeTab === RedemptionCodeTabs.RedemptionCode) {
					setIfRefetchDataOfRedemptionCodes(true);
				} else {
					setIfRefetchDataOfMgCodes(true);
				}
			})
			.catch(() => {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: t('api.defaultError'),
				});
			})
			.finally(() => {
				setIsBatchDeleteing(false);
			});
	};

	function handleOpenCreateModal() {
		createModalOpen();
	}
	useEffect(()=> {
		if(projectInfo?.productId != undefined){
			const res = api.queryMgCodesByContentProvider(
				undefined,
				undefined,
				projectInfo.productId,
				undefined,
				1,
				1
			).then((res) => {
				setHasMgCodes(res.totalCount != undefined && res.totalCount > 0);
			});
		}
		
	},[projectInfo]);
	if (!projectInfo) {
		return null;
	}
	
	

	return (
		<div className="page-wrapper scroll-box">
			<div>
				<div className="page-head">
					<div>
						<h2 className="h2">{t('redemptionCodesPage.Header')}</h2>
					</div>

					<div className={styles.buttonContainer}>
						{!hasNoData && (
							<>
								{selectedRows.length > 0 && (
									<Button
										data-testid="batchDeleteBtn"
										danger
										icon={<DeleteOutlined />}
										disabled={selectedRows.length === 0}
										loading={isBatchDeleteing}
										onClick={handleBatchDelete}
									>
										{t('redemptionCodesPage.Header.Delete', {
											total: selectedRows.length,
										})}
									</Button>
								)}
								<Button
									data-testid="downloadBtn"
									type="primary"
									className="color-dark"
									loading={isCSVDownloading}
									onClick={handleExportClick}
								>
									{t('redemptionCodesPage.Header.ExportAsCSV')}
								</Button>
							</>
						)}

						{projectInfo?.status == ProductStatus.Released &&
							activeTab === RedemptionCodeTabs.RedemptionCode && (
								<Button
									data-testid="generateCodesBtn"
									type="primary"
									onClick={() => handleOpenCreateModal()}
								>
									{t('redemptionCodesPage.Header.Create')}
								</Button>
							)}
					</div>
				</div>
				<div>
					<Tabs
						activeKey={activeTab}
						onChange={(key) => setActiveTab(key as RedemptionCodeTabs)}
						size="large"
					>
						<TabPane
							tab={t('redemptionCodesPage.type.redemptionCode')}
							key={RedemptionCodeTabs.RedemptionCode}
						>
							<RedemptionCodesTab
								projectId={projectId}
								isActiveTab={activeTab === RedemptionCodeTabs.RedemptionCode}
								setSelectedRowsCallBackFunction={setSelectedRows}
								allowRefetchData={ifRefetchDataOfOmniOnline}
								resetAllowRefetchData={() => {
									setIfRefetchDataOfRedemptionCodes(false);
								}}
								setSorterRefForExporting={(sorter) => {
									setRedemptionCodeSorterRef(sorter);
								}}
								setHasNoRedemptionCodeBackFunction={(status) => {
									setHasNoData(status);
								}}
							/>
						</TabPane>
						{hasMgCodes &&(
							<TabPane
								tab={t('redemptionCodesPage.type.minimumGuaranteedCode')}
								key={RedemptionCodeTabs.MinimumGuaranteedCode}
							>
								<MinimumGuaranteedCodesTab
									productId={projectInfo?.productId!}
									isActiveTab={
										activeTab === RedemptionCodeTabs.MinimumGuaranteedCode
									}
									setSelectedRowsCallBackFunction={setSelectedRows}
									allowRefetchData={ifRefetchDataOfMgCodes}
									resetAllowRefetchData={() => {
										setIfRefetchDataOfMgCodes(false);
									}}
									setSorterRefForExporting={(sorter) => {
										setMgCodesSorterRef(sorter);
									}}
									setHasNoRedemptionCodeBackFunction={(status) => {
										setHasNoData(status);
									}}
									setHasMgCodesCallbackFunction={(status) => {
										setHasMgCodes(status);
									}}
								/>
							</TabPane>
						)}
					</Tabs>
				</div>
				<RedemptionCodesCreateModal
					projectId={projectId}
					visible={createModalVisible}
					setVisible={(visible) => setCreateModalVisible(visible)}
					afterSubmit={async () => {
						setIfRefetchDataOfRedemptionCodes(true);
						setIfRefetchDataOfMgCodes(true);
						setCreateModalVisible(false);
					}}
				/>
			</div>
		</div>
	);
}
